/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useState } from "react";
import {
  Box,
  Button,
  Collapse,
  IconButton,
  Tab,
  Table,
  TableBody,
  TableFooter,
  TableRow,
  Tabs,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DownloadIcon from "@mui/icons-material/Download";
import dayjs from "dayjs";
import { useQuery } from "@tanstack/react-query";
import api from "../../../api";
import csvLoader from "../../../assets/images/csv2.gif";
import { Loader } from "../../../components/loader";
import { LightTooltip, StyledInfoIcon } from "../../../components/statistics_cards/styles";
import {
  StyledTableBodyCell,
  StyledTableHead,
  StyledTableHeadCell,
  StyledTableRow,
} from "../../../components/index_table/styles";
import { useNotificationContext } from "../../../contexts/notificationContext";
import { Query_Keys } from "../../../lib/queryKeys";
import { CSV_EXPORT_PATH, downloadExport, withApiQuery } from "../../../utils/reportExport";
import {
  PERQ_DISCOUNT_CLASSES,
  PERQ_DISCOUNT_CLASS_LABELS,
  formatMoney,
  formatPercent,
  getPerqReportApiFilters,
  getSingleDiscountClass,
} from "../../../utils/perqReportDrilldown";

const BORDER_COLOR = "#eee";
const DIVIDER_COLOR = "rgba(224, 224, 224, 1)";
const ALL_TAB = "all";

const BREAKDOWN_TABS = [
  { value: ALL_TAB, label: "All" },
  ...PERQ_DISCOUNT_CLASSES.map((value) => ({ value, label: PERQ_DISCOUNT_CLASS_LABELS[value] })),
];

// Fixed height, so the orders table below keeps room.
const BODY_HEIGHT = "min(360px, 40vh)";

const PROFITABILITY_TOOLTIP =
  "Check subtotal after discounts, before tax and tip, counted once for each time this discount was applied.";
const PRE_SALES_TOOLTIP = "Amount of all orders where this perq was applied at least once";
const EMPTY_MESSAGE = "No records for the selected filters.";
const LOAD_ERROR_MESSAGE = "Failed to load the perq breakdown.";
const CSV_ERROR_MESSAGE = "Failed to download CSV";

// Narrower than this, the section scrolls sideways.
const TABLE_MIN_WIDTH = 880;

// Shared by the rows and Total tables so their columns line up.
const COLUMNS = [
  { key: "name", label: "Discount Name", width: "21%" },
  { key: "classification", label: "Classification", width: "12%" },
  { key: "count", label: "Count", width: "8%" },
  { key: "pre_sales", label: "Amount before discount", width: "15%", tooltip: PRE_SALES_TOOLTIP },
  { key: "amount", label: "Discount Amount", width: "15%" },
  { key: "profitability", label: "Profitability", width: "14%", tooltip: PROFITABILITY_TOOLTIP },
  { key: "percent_of_total_sales", label: "% of Total Sales", width: "15%" },
];

const tableStyles = { tableLayout: "fixed", borderCollapse: "separate", borderSpacing: 0 };

// Sticks as one group, so rows never show through gaps between cells.
const stickyHeadStyles = {
  position: "sticky",
  top: 0,
  zIndex: 2,
  "& th": { backgroundColor: "white", borderBottom: 0, boxShadow: `inset 0 -1px 0 ${DIVIDER_COLOR}` },
};

// Same scrollbar gutter on both tables keeps their widths equal.
const gutterStyles = { scrollbarGutter: "stable" };

const ColumnWidths = () => (
  <colgroup>
    {COLUMNS.map((column) => (
      <col key={column.key} style={{ width: column.width }} />
    ))}
  </colgroup>
);

// Page filters plus the selected tab; All sends no class.
export const getBreakdownApiParams = (appliedFilters, breakdownClass) => ({
  ...getPerqReportApiFilters(appliedFilters),
  ...(breakdownClass && breakdownClass !== ALL_TAB ? { breakdown_classes: [breakdownClass] } : {}),
});

const MetricCells = ({ row }) => (
  <React.Fragment>
    <StyledTableBodyCell variant="body">{row?.count ?? 0}</StyledTableBodyCell>
    <StyledTableBodyCell variant="body">{formatMoney(row?.pre_sales)}</StyledTableBodyCell>
    <StyledTableBodyCell variant="body">{formatMoney(row?.amount)}</StyledTableBodyCell>
    <StyledTableBodyCell variant="body">{formatMoney(row?.profitability)}</StyledTableBodyCell>
    <StyledTableBodyCell variant="body">{formatPercent(row?.percent_of_total_sales)}</StyledTableBodyCell>
  </React.Fragment>
);

const StatusMessage = ({ testId, children }) => (
  <Box
    data-testid={testId}
    sx={{ flex: 1, px: 2, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1 }}
  >
    {children}
  </Box>
);

export const PerqReportBreakdown = ({ appliedFilters }) => {
  const showNotification = useNotificationContext();
  const [expanded, setExpanded] = useState(false);
  // Opens on the one classification picked in the page filters (or a tile), else All.
  const appliedTab = getSingleDiscountClass(appliedFilters) || ALL_TAB;
  const [breakdownClass, setBreakdownClass] = useState(appliedTab);
  const [syncedTab, setSyncedTab] = useState(appliedTab);

  // Follows a new classification during render, so no request goes out with the old tab.
  if (syncedTab !== appliedTab) {
    setSyncedTab(appliedTab);
    setBreakdownClass(appliedTab);
  }
  const [csvLoading, setCsvLoading] = useState(false);

  const { data, isFetching, error, refetch } = useQuery({
    queryKey: Query_Keys.discount_report_breakdown({ queryFilter: appliedFilters, breakdownClass }),
    // Params are built per fetch, so a background refetch uses the current date range.
    queryFn: async () => {
      const response = await api.get("/v1/admin/discount_reports/breakdown", {
        params: getBreakdownApiParams(appliedFilters, breakdownClass),
      });
      return response?.data || {};
    },
    // Fetches only once the section is opened.
    enabled: expanded,
    staleTime: 10 * 60 * 1000,
  });

  const rows = Array.isArray(data?.rows) ? data.rows : [];
  // With no data yet, keeps the loader, not the empty text, while the fetch waits (e.g. offline).
  const loading = isFetching || (!data && !error);
  const csvDisabled = csvLoading || isFetching || Boolean(error) || rows.length === 0;

  const handleExportCsv = async () => {
    if (csvLoading) return;
    setCsvLoading(true);
    try {
      await downloadExport({
        url: withApiQuery(CSV_EXPORT_PATH, {
          report: "discount_report_breakdown",
          ...getBreakdownApiParams(appliedFilters, breakdownClass),
        }),
        // Used when the browser can't read the server's filename (CORS hides it); dated in the admin's local time.
        fallbackFilename: `perq_breakdown_${dayjs().format("YYYY-MM-DD")}.csv`,
      });
      showNotification("CSV downloaded successfully", "success");
    } catch (exportError) {
      showNotification(exportError?.message || CSV_ERROR_MESSAGE, "error");
    } finally {
      setCsvLoading(false);
    }
  };

  const renderBody = () => {
    if (loading) {
      return (
        <Box
          data-testid="perq-breakdown-loader"
          role="status"
          aria-live="polite"
          aria-label="Loading perq breakdown"
          sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}
        >
          <Loader />
        </Box>
      );
    }

    if (error) {
      return (
        <StatusMessage testId="perq-breakdown-error">
          <Typography sx={{ fontSize: "14px", color: "#b91c1c" }}>
            {error?.response?.data?.error || LOAD_ERROR_MESSAGE}
          </Typography>
          <Button size="small" onClick={() => refetch()}>
            Retry
          </Button>
        </StatusMessage>
      );
    }

    if (rows.length === 0) {
      return (
        <StatusMessage testId="perq-breakdown-empty">
          <Typography sx={{ fontSize: "14px", color: "#71717a" }}>{EMPTY_MESSAGE}</Typography>
        </StatusMessage>
      );
    }

    return (
      <React.Fragment>
        <Box data-testid="perq-breakdown-scroll" sx={{ flex: 1, minHeight: 0, overflowY: "auto", ...gutterStyles }}>
          <Table data-testid="perq-breakdown-table" sx={tableStyles}>
            <ColumnWidths />
            <StyledTableHead sx={stickyHeadStyles}>
              <TableRow>
                {/* Headers may wrap, so a long label stays readable on narrow screens instead of being cut off. */}
                {COLUMNS.map((column) => (
                  <StyledTableHeadCell key={column.key} sx={{ whiteSpace: "normal" }}>
                    {column.tooltip ? (
                      <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                        <LightTooltip title={column.tooltip} placement="top">
                          <StyledInfoIcon data-testid={`perq-breakdown-${column.key}-info`} />
                        </LightTooltip>
                        {column.label}
                      </Box>
                    ) : (
                      column.label
                    )}
                  </StyledTableHeadCell>
                ))}
              </TableRow>
            </StyledTableHead>
            <TableBody sx={{ "& tr:last-of-type td": { borderBottom: 0 } }}>
              {rows.map((row, index) => (
                <StyledTableRow key={`${row?.name}-${row?.classification}-${index}`} data-testid={`perq-breakdown-row-${index}`}>
                  <StyledTableBodyCell title={row?.name}>{row?.name}</StyledTableBodyCell>
                  <StyledTableBodyCell>{PERQ_DISCOUNT_CLASS_LABELS[row?.classification] || "-"}</StyledTableBodyCell>
                  <MetricCells row={row} />
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </Box>

        {/* Outside the scroll area, so rows never show behind the Total. */}
        <Box sx={{ flexShrink: 0, overflow: "hidden", borderTop: `1px solid ${DIVIDER_COLOR}`, ...gutterStyles }}>
          <Table sx={tableStyles}>
            <ColumnWidths />
            <TableFooter data-testid="perq-breakdown-total" sx={{ "& td": { borderBottom: 0, fontWeight: 600 } }}>
              <TableRow>
                <StyledTableBodyCell variant="body" colSpan={2}>
                  Total
                </StyledTableBodyCell>
                <MetricCells row={data?.totals} />
              </TableRow>
            </TableFooter>
          </Table>
        </Box>
      </React.Fragment>
    );
  };

  return (
    <Box
      data-testid="perq-breakdown"
      sx={{ mb: "20px", border: `1px solid ${BORDER_COLOR}`, borderRadius: "10px", backgroundColor: "white", overflow: "hidden" }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.5, py: 1 }}>
        <Typography sx={{ fontSize: "16px", fontWeight: 600, color: "#18181b" }}>Perq Breakdown</Typography>
        <IconButton
          data-testid="perq-breakdown-toggle"
          aria-label={expanded ? "Collapse perq breakdown" : "Expand perq breakdown"}
          aria-expanded={expanded}
          aria-controls={expanded ? "perq-breakdown-content" : undefined}
          onClick={() => setExpanded((prev) => !prev)}
        >
          <ExpandMoreIcon
            sx={{ color: "#52525b", transition: "transform 0.2s ease", transform: expanded ? "rotate(180deg)" : "none" }}
          />
        </IconButton>
      </Box>

      <Collapse in={expanded} timeout="auto" unmountOnExit id="perq-breakdown-content">
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            px: 1,
            borderTop: `1px solid ${BORDER_COLOR}`,
            borderBottom: `1px solid ${BORDER_COLOR}`,
          }}
        >
          <Tabs
            value={breakdownClass}
            onChange={(_, value) => setBreakdownClass(value)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            aria-label="Discount classification"
            TabIndicatorProps={{ style: { backgroundColor: "#ff653a" } }}
            sx={{ minHeight: 40, "& .MuiTab-root": { minHeight: 40, textTransform: "none" }, "& .MuiTab-root.Mui-selected": { color: "#18181b", fontWeight: 600 } }}
          >
            {BREAKDOWN_TABS.map((tab) => (
              <Tab key={tab.value} value={tab.value} label={tab.label} data-testid={`perq-breakdown-tab-${tab.value}`} />
            ))}
          </Tabs>
          {csvLoading ? (
            <IconButton disableRipple aria-label="Downloading CSV">
              <img style={{ height: 24 }} src={csvLoader} alt="loading..." />
            </IconButton>
          ) : (
            <IconButton
              data-testid="perq-breakdown-export-csv"
              aria-label="Download breakdown CSV"
              disabled={csvDisabled}
              onClick={handleExportCsv}
              sx={{ color: "#2b4fb9" }}
            >
              <DownloadIcon />
            </IconButton>
          )}
        </Box>

        <Box sx={{ height: BODY_HEIGHT, overflowX: "auto", overflowY: "hidden" }}>
          <Box sx={{ height: "100%", minWidth: TABLE_MIN_WIDTH, display: "flex", flexDirection: "column" }}>
            {renderBody()}
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};
